import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getTipologieRifiutoProdotto, createTipologiaRifiutoProdotto, updateTipologiaRifiutoProdotto,
  getCerRifiutiProdotti,
  type TipologiaRifiutoProdotto, type CerRifiutoProdotto,
} from '../../api/settings';
import SettingsCrudSection, { type FieldDef, type FieldOption } from './SettingsCrudSection';

/**
 * Tipologie del rifiuto prodotto (V74): la DEFINIZIONE commerciale sotto il
 * codice EER — «Alluminio FV», «Polvere di alluminio», «Vetro fine turbina».
 *
 * ASSE DIVERSO dal «materiale prodotto» della scheda accanto: quello è il nome
 * dell'uscita dell'impianto (cosa esce dalla linea), questa dipende da cosa è
 * stato trattato a monte. Alluminio FV e alluminio infissi hanno stesso CER e
 * stessa uscita, e si vendono a prezzi diversi.
 *
 * RBAC dei Settings, NON il gate fiscale: la tipologia non cambia aliquota,
 * Natura né dicitura.
 */
export default function TipologieRifiutoProdottoSettings() {
  const { t } = useTranslation();
  const [items, setItems] = useState<TipologiaRifiutoProdotto[]>([]);
  const [cerOptions, setCerOptions] = useState<CerRifiutoProdotto[]>([]);
  const [loading, setLoading] = useState(true);
  // Filtro per CER: il catalogo cresce per codice (sotto 19 12 03 ce ne sono
  // sette), e senza filtro la tabella diventa un elenco piatto in cui il
  // raggruppamento che conta — quale materiale sta sotto quale codice — sparisce.
  const [filtroCer, setFiltroCer] = useState('');

  const reload = useCallback(() => {
    void (async () => {
      setLoading(true);
      try { setItems(await getTipologieRifiutoProdotto(false)); } finally { setLoading(false); }
    })();
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { void (async () => { setCerOptions(await getCerRifiutiProdotti(true)); })(); }, []);

  const etichettaCer = (c: { cer: string; nome_descrittivo: string | null }) =>
    (c.nome_descrittivo ? `${c.cer} — ${c.nome_descrittivo}` : c.cer);
  const cerFieldOptions: FieldOption[] = cerOptions.map(
    (c) => ({ value: c.id, label: etichettaCer(c) }));
  // Mappa id → label anche dai dati delle righe: copre un CER referenziato ma
  // non più attivo, che non arriverebbe dalle opzioni.
  const labelById = new Map<string, string>();
  for (const c of cerOptions) labelById.set(c.id, etichettaCer(c));
  for (const it of items) labelById.set(it.cer_rifiuto_id, etichettaCer(it));

  const fields: FieldDef[] = [
    { key: 'nome', label: t('settings.tipologie.nome'), kind: 'text', required: true,
      placeholder: t('settings.tipologie.nome_placeholder') },
    { key: 'cer_rifiuto_id', label: t('settings.tipologie.cer_rifiuto'), kind: 'select',
      required: true, optionsKey: 'cer_rifiuto', display: (v) => labelById.get(v ?? '') ?? '—' },
  ];

  const banner = cerOptions.length === 0 ? (
    <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
      {t('settings.tipologie.banner')}
    </div>
  ) : (
    <div className="flex items-center gap-2">
      <label className="text-sm text-ink-mid" htmlFor="tipologie-filtro-cer">
        {t('settings.tipologie.filtro_cer')}
      </label>
      <select id="tipologie-filtro-cer" data-testid="tipologie-filtro-cer"
        className="text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white"
        value={filtroCer} onChange={(e) => setFiltroCer(e.target.value)}>
        <option value="">{t('settings.tipologie.filtro_tutti')}</option>
        {cerOptions.map((c) => <option key={c.id} value={c.id}>{etichettaCer(c)}</option>)}
      </select>
    </div>
  );

  const righe = filtroCer ? items.filter((i) => i.cer_rifiuto_id === filtroCer) : items;

  return (
    <SettingsCrudSection<TipologiaRifiutoProdotto>
      description={t('settings.tipologie.descrizione')}
      fields={fields}
      rows={righe}
      loading={loading}
      reload={reload}
      dynamicOptions={{ cer_rifiuto: cerFieldOptions }}
      testidPrefix="tipologia"
      entityLabel={t('settings.tipologie.entita')}
      emptyMsg={t('settings.tipologie.vuoto')}
      statoLabels={{
        attivo: t('settings.tipologie.stato_attiva'),
        disattivato: t('settings.tipologie.stato_disattivata'),
      }}
      banner={banner}
      onCreate={async (v) => {
        await createTipologiaRifiutoProdotto({
          nome: v.nome.trim(), cer_rifiuto_id: v.cer_rifiuto_id });
      }}
      onUpdate={async (id, v) => {
        await updateTipologiaRifiutoProdotto(id, {
          nome: v.nome.trim(), cer_rifiuto_id: v.cer_rifiuto_id });
      }}
      onToggle={async (it) => { await updateTipologiaRifiutoProdotto(it.id, { attivo: !it.attivo }); }}
    />
  );
}
